import { Provider } from 'react-redux'
import { ApolloProvider } from '@apollo/client'
import { AppWrapper } from '@engyalo/delivery-ui-components'
import { datadogRum } from '@datadog/browser-rum'
import groundcover from '@groundcover/browser'
import Hotjar from '@hotjar/browser'
import Routes from './routes/index'
import Splash from './components/Splash'
import AppTheme from './containers/AppThemeConnected'
import apolloClientConfig from './config/apolloClient'
import RouterParams from './components/RouterParams'
import GlobalLoader from './components/GlobalLoader'
import store from './redux/store'
import { redactRumEventUrls } from './utils/redactRumUrls'
import 'swiper/css'
import 'swiper/css/bundle'

if (process.env.REACT_APP_DATADOG_CLIENT_TOKEN) {
  /*
   * **The COM-855 redaction (AC #5).** `beforeSend` hands every event to
   * `utils/redactRumUrls.ts`, which replaces the value of any `token`- or
   * `jwt`-named parameter in every URL field the SDK lets a client rewrite
   * (five of them today) and keeps the event.
   * `services/postCreateOrder.tsx` puts the workflow JWT in the query string
   * of both stephook POSTs and RUM transmits `resource.url` verbatim, so this
   * is what stops that credential reaching the intake — the options below
   * never did. `resource.url` is the only one of the five that demonstrably
   * carries it: the `view.*` fields are covered because they are stamped from
   * the webview's own URL onto every event of every type, not because a
   * cinnamon parameter is a credential today. Nothing here touches the event
   * `context`, where `utils/dataDog.ts` puts `searchParams` and `viewName`
   * off `window.location`, nor a credential named outside the denylist; that
   * function's docstring has the per-field reasoning.
   *
   * **The guard is on retention; collection was never gated.**
   * `startResourceCollection` runs unconditionally (`boot/startRum.js`), so
   * stephook resource events are batched and sent either way. What
   * `trackResources` changes is a flag on them: `computeIndexingInfo`
   * (`domain/resource/resourceCollection.js`) stamps
   * `_dd.discarded: !session || !session.resourceAllowed`, and the only
   * client-side reader of it is a counter in `trackEventCounts.js` — nothing
   * drops the event. That much is verified here. **That Datadog's backend
   * treats the flag as an indexing decision and retains none of them is
   * inference**, about a server this repo cannot read.
   *
   * Keep retention off while the credential is in a URL at all: a two-name
   * denylist stretched over every resource URL the app touches is thinner
   * cover than the redaction on its own suggests.
   *
   * **Three edits switch retention on, and the third is a deletion.**
   * `trackResources: true` does it outright. Raising `premiumSampleRate` does
   * it while reading as a sampling change. **Deleting the
   * `premiumSampleRate: 0` line below** does it as well, and is the likeliest
   * of the three because the option is legacy: `domain/configuration.js`
   * resolves it under a `// TODO remove fallback in next major`, and
   * `sessionReplaySampleRate` supersedes it with an `Ignoring Premium Sample
   * Rate` warning. With both absent, `sessionReplaySampleRate ??
   * premiumSampleRate ?? 100` resolves to 100, `oldPlansBehavior` stays true,
   * `performDraw(100)` never fails, every session reaches
   * `plan === WITH_SESSION_REPLAY`, and `resourceAllowed`
   * (`domain/rumSessionManager.js`) is true for every session. The last two
   * bite only while `sessionReplaySampleRate` is unset: adopting it makes
   * `oldPlansBehavior` false, and `resourceAllowed` stays false even at 100%
   * replay. All of this is derived from `@datadog/browser-rum`, pinned exactly
   * at `4.50.1` with no caret in `packages/cinnamon/package.json`;
   * `oldPlansBehavior` is the legacy branch the SDK plans to delete, so a
   * major bump needs this re-derived rather than re-read.
   *
   * AC #1 — moving the token into a request header — is still open, and is not
   * local to this repo: the workflow and botrunner endpoints have to accept it
   * off the query string, and `postStephook` is on every storefront's checkout
   * path. Whoever lands it should come back here and lift this guard.
   */
  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APP_ID,
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    env: process.env.REACT_APP_DATADOG_ENV,
    service: process.env.REACT_APP_DATADOG_SERVICE_NAME,
    site: 'us5.datadoghq.com',
    trackInteractions: true,
    // Deleting this line turns resource retention on — see above (COM-855)
    premiumSampleRate: 0,
    trackViewsManually: Boolean(
      process.env.REACT_APP_DATADOG_ENABLE_TRACKING_VIEW,
    ),
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: redactRumEventUrls,
  })
}

/*
 * groundcover RUM, running alongside Datadog rather than replacing it (DOPS-1802).
 * Both SDKs collect independently: Datadog keeps every monitor and dashboard that
 * exists today working while groundcover accumulates enough sessions for those to
 * be rebuilt against it. Nothing here changes the Datadog path above.
 *
 * Inert unless REACT_APP_GROUNDCOVER_RUM_API_KEY is set, so merging this ships no
 * behaviour change until an environment is deliberately configured.
 *
 * **The COM-855 concern applies here too, and is covered differently.** The
 * workflow JWT reaches `resource.url` through the stephook POSTs in
 * `services/postCreateOrder.tsx`; on the Datadog side `redactRumEventUrls`
 * rewrites it. That function is written against Datadog's event shape and does
 * not transfer, so this SDK relies on its own mechanism: `privacy.sensitiveKeys`
 * names the same two parameters, on top of the default `mask-sensitive` level,
 * which already strips auth headers and the OAuth query parameters. A generic
 * `token` or `jwt` parameter is NOT in that default set — naming them explicitly
 * is what covers the cinnamon case.
 *
 * Verify before enabling anywhere that carries real orders: send a checkout
 * through, open the session in groundcover, confirm the stephook resource URL
 * shows the parameter redacted. As with the Datadog block above, the guard is on
 * retention rather than collection, so that check is not optional.
 */
if (process.env.REACT_APP_GROUNDCOVER_RUM_API_KEY) {
  groundcover.init({
    // The ingestion key stays in the environment, like the Datadog client token
    // above. `dsn` and `cluster` are tenant endpoints rather than credentials, so
    // they sit here for the same reason `site` does in the Datadog block.
    apiKey: process.env.REACT_APP_GROUNDCOVER_RUM_API_KEY,
    dsn: 'https://kjv7tl1gb.platform.grcv.io',
    cluster: 'yalo-backend-gcp',
    appId: process.env.REACT_APP_DATADOG_SERVICE_NAME,
    environment: process.env.REACT_APP_DATADOG_ENV,
    // Source maps resolve against this; CI has to stamp it or stack traces in
    // groundcover stay minified. Datadog never had it set here, so there is no
    // existing value to reuse.
    releaseId: process.env.REACT_APP_RELEASE_ID,
    options: {
      privacy: {
        level: 'mask-sensitive',
        sensitiveKeys: ['token', 'jwt'],
      },
    },
  })
}

Hotjar.init(
  Number(process.env.REACT_APP_HOTJAR_ID),
  Number(process.env.REACT_APP_HOTJAR_VERSION),
)

const App = () => {
  return (
    <Provider store={store}>
      <ApolloProvider client={apolloClientConfig}>
        <AppTheme>
          <AppWrapper>
            <Routes>
              <RouterParams />
            </Routes>
            <Splash />
            <GlobalLoader />
          </AppWrapper>
        </AppTheme>
      </ApolloProvider>
    </Provider>
  )
}

export default App
